<script lang="ts" setup>
    import { computed, onMounted, reactive, watch } from 'vue';
    import '../../../css/choice.css';
    import * as choiceApi from '../../lib/ChoiceApi';
    import { ModalStyles, popAlert } from '../../lib/Notification';
    import { NoTzDate } from '../../lib/DateUtils';
    import { ButtonDisplaySize, ButtonDisplayStyle, DateDisplayFormat, ModalSize } from '../../Enums/UiEnums';
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import vSelect from 'vue-select';
    import ChoiceButton from '../../components/Buttons/ChoiceButton.vue';
    import { IAppointment, IAppointmentLocation, IAppointmentResource, IAppointmentStatus, IAppointmentType } from '../../lib/Models/AppointmentModel';
    import GroupedRowsTable from '../../components/Table/GroupedRowsTable.vue';
    import { IGroupedRowItem, IGroupedRowSortDirection } from '../../lib/Models/GroupedRowsTable';
    import ChoiceButtonGroup from '../../components/ChoiceButtonGroup.vue';
    import { IChoiceButtonGroupItem } from '../../lib/Models/ChoiceButtonGroup';
    import { IEntityInfoById } from '../../lib/Models/EntityModel';
    import { DatabaseIdTypes, PortalType } from '../../Enums/ChoiceEnums';
    import ModalDialog from '../../components/ModalDialog.vue';
    import CancelCommentLegacy from '../Screens/CancelCommentLegacy.vue';
    import PatientHeader from '../../components/Choice/Patient/PatientHeader.vue';

    const props = defineProps<{
        patientEid: number
    }>();

    const pageData = reactive<{
        patientInfo?: IEntityInfoById,
        appointmentStatuses: IAppointmentStatus[],
        appointmentTypes: IAppointmentType[],
        appointmentResources: IAppointmentResource[],
        appointmentLocations: IAppointmentLocation[],
        appointments?: IAppointment[],
        selectedAppointmentId?: number,
        showCommentCancel: boolean,
        pendingRowView?: IGroupedRowItem
    }>({
        appointmentStatuses: [],
        appointmentTypes: [],
        appointmentResources: [],
        appointmentLocations: [],
        appointments: undefined,
        showCommentCancel: false
    });

    const filters = reactive<{
        startDate?: NoTzDate,
        endDate?: NoTzDate,
        statuses: number[],
        types: number[],
        resources: number[],
        locations: number[],
        quickFilter?: string
    }>({
        statuses: [],
        types: [],
        resources: [],
        locations: []
    });

    const quickFilterOptions: IChoiceButtonGroupItem[] = [
        { label: "All", value: "All", click: () => { resetFilters(); } },
        { label: "No Show", value: "No-Show", click: () => { setQuickFilter("No-Show"); } },
        { label: "Cancelled", value: "Cancelled", click: () => { setQuickFilter("Cancelled"); } },
        { label: "Checked Out", value: "Checked-Out", click: () => { setQuickFilter("Checked-Out"); } }
    ];

    enum DayLookback {
        Today = 0,
        Yesterday = 1,
        TwoDays = 2,
        TwoWeeks = 14,
        ThreeWeeks = 21,
        Thirty = 30,
    } 

    const filteredAppointments = computed(() => {
        let result = pageData.appointments;
        if (result) {
            // apply date filters
            if (filters.startDate) {
                result = result.filter(appointment => filters.startDate && new Date(appointment.appointmentDate) >= new Date(filters.startDate));
            }
            if (filters.endDate) {
                result = result.filter(appointment => filters.endDate && new Date(appointment.appointmentEndDate) <= new Date(filters.endDate));
            }
            // apply remaining filters
            if (filters.statuses?.length) {
                result = result.filter(appointment => filters.statuses && filters.statuses.some(statusId => statusId === appointment.statusId));
            }
            if (filters.types?.length) {
                result = result.filter(appointment => filters.types && filters.types.some(typeId => typeId === appointment.appointmentTypeId));
            }
            if (filters.resources?.length) {
                result = result.filter(appointment => filters.resources && filters.resources.some(entityId => entityId === appointment.scheduledEntityId));
            }
            if (filters.locations?.length) {
                result = result.filter(appointment => filters.locations && filters.locations.some(locationId => locationId === appointment.locationEntityId));
            }
        }
        return result;
    });

    onMounted(async () => {
        if (!props.patientEid) {
            popAlert("Patient identifier was not provided.", "Patient Appointments", ModalStyles.info);
            return;
        }
        MessageLib.Scheduler.sink.schedulerReady(schedulerReadySink);
        MessageLib.Scheduler.sink.cancelComment(cancelCommentSink);

        const [
            patientInfo,
            appointmentStatuses,
            appointmentTypes,
            appointmentResources,
            appointmentLocations,
            appointments
        ] = await Promise.all([
            choiceApi.entityInfo.getEntityInfoByDatabaseId({ idType: DatabaseIdTypes.entity_id, id: props.patientEid }),
            choiceApi.appointment.appointmentStatuses(),
            choiceApi.appointment.appointmentTypes(),
            choiceApi.appointment.appointmentResources(),
            choiceApi.appointment.appointmentLocations(),
            choiceApi.appointment.appointments(props.patientEid)
        ]);

        pageData.patientInfo = patientInfo;
        pageData.appointmentStatuses = appointmentStatuses;
        pageData.appointmentTypes = appointmentTypes;
        pageData.appointmentResources = appointmentResources;
        pageData.appointmentLocations = appointmentLocations;
        pageData.appointments = appointments;
    });

    watch(
	    () => filters,
        () => {
            if (!filterIsSet()) {
                resetFilters();
            }
            if (!filters.statuses.length || filters.statuses.length > 1) {
                filters.quickFilter = undefined;
            }
        },
        { deep: true }
    );

    function filterIsSet(): boolean {
        return filters.startDate != undefined
                || filters.endDate != undefined
                || filters.statuses?.length > 0
                || filters.types?.length > 0
                || filters.resources?.length > 0
                || filters.locations?.length > 0
                || filters.quickFilter != undefined;
    }

    function resetFilters(): void {
        filters.startDate = undefined;
        filters.endDate = undefined;
        filters.statuses = [];
        filters.types = [];
        filters.resources = [];
        filters.locations = [];
        filters.quickFilter = undefined;
    }

    function getDaysBetween(date1: Date, date2: Date): number {
        const timeDiff = date2.getTime() - date1.getTime(); // Difference in milliseconds
        return timeDiff / (1000 * 3600 * 24); // Convert milliseconds to days
    }

    function getAppointmentChiefAndComment(appointment: IAppointment): string {
        const parts: string[] = [];
        if (appointment.chiefComplaint) {
            parts.push(`[Chief] ${appointment.chiefComplaint}`);
        }
        if (appointment.comment) {
            parts.push(`[Comment] ${appointment.comment}`);
        }
        return parts.join(' / ');
    }

    function buildGroupedRows(appointments?: IAppointment[]): IGroupedRowItem[] {
        let result: IGroupedRowItem[] = [];
        appointments?.forEach(
            appointment => { 
                result.push(
                    {
                        appointmentId: appointment.appointmentId.toString(),
                        appointmentDate: appointment.appointmentDate,
                        startTime: appointment.startTimeFormatted ?? "",
                        endTime: appointment.endTimeFormatted ?? "",
                        scheduled: pageData.appointmentResources.find(resource => resource.entityId === appointment.scheduledEntityId)?.resourceName ?? "",
                        location: pageData.appointmentLocations.find(location => location.locationId === appointment.locationEntityId)?.locationName ?? "",
                        status: pageData.appointmentStatuses.find(status => status.appointment_status_id === appointment.statusId)?.appointment_status_description ?? "",
                        type: pageData.appointmentTypes.find(type => type.appointmentTypeId == appointment.appointmentTypeId)?.appointmentName ?? "",
                        comment: getAppointmentChiefAndComment(appointment),
                    }
                )
            }
        )
        return result;
    }

    function getFutureAppointments(): IGroupedRowItem[] {
        let appointments = filteredAppointments.value?.filter(
            appointment => new Date(appointment.appointmentDate) > new Date()
        );
        return buildGroupedRows(appointments);
    }

    function getAppointments(startDayRange: number, endDayRange?: number): IGroupedRowItem[] {
        let appointments = filteredAppointments.value?.filter(
            appointment => {
                let daysBetween = getDaysBetween(new Date(appointment.appointmentDate), new Date());
                return daysBetween >= startDayRange && (endDayRange === undefined || daysBetween < endDayRange);
            }
        );
        return buildGroupedRows(appointments);
    }

    function getAppointmentCountByStatus(statusDescription: string): number {
        let statusId = pageData.appointmentStatuses.find(status => status.appointment_status_description == statusDescription)?.appointment_status_id;
        return pageData.appointments?.filter(appointment => appointment.statusId == statusId)?.length ?? 0;
    }

    function setQuickFilter(statusDescription: string): void {
        filters.quickFilter = statusDescription;
        let statusId = pageData.appointmentStatuses.find(status => status.appointment_status_description == statusDescription)?.appointment_status_id;
        if (statusId) {
            filters.statuses = [];
            filters.statuses.push(statusId);
        }
    }

    function sortByStartTime(rows: IGroupedRowItem[], direction: IGroupedRowSortDirection): void {
        if (direction === IGroupedRowSortDirection.ASC) {
            rows.sort((row1, row2) => new Date(row1.startTime).getTime() - new Date(row2.startTime).getTime());
        }
        else if (direction === IGroupedRowSortDirection.DESC) {
            rows.sort((row1, row2) => new Date(row2.startTime).getTime() - new Date(row1.startTime).getTime());
        }
    }

    function sortByScheduled(rows: IGroupedRowItem[], direction: IGroupedRowSortDirection): void {
        if (direction === IGroupedRowSortDirection.ASC) {
            rows.sort((row1, row2) => row1.scheduled.localeCompare(row2.scheduled));
        }
        else if (direction === IGroupedRowSortDirection.DESC) {
            rows.sort((row1, row2) => row2.scheduled.localeCompare(row1.scheduled));
        }
    }

    function sortByLocation(rows: IGroupedRowItem[], direction: IGroupedRowSortDirection): void {
        if (direction === IGroupedRowSortDirection.ASC) {
            rows.sort((row1, row2) => row1.location.localeCompare(row2.location));
        }
        else if (direction === IGroupedRowSortDirection.DESC) {
            rows.sort((row1, row2) => row2.location.localeCompare(row1.location));
        }
    }

    function sortByStatus(rows: IGroupedRowItem[], direction: IGroupedRowSortDirection): void {
        if (direction === IGroupedRowSortDirection.ASC) {
            rows.sort((row1, row2) => row1.status.localeCompare(row2.status));
        }
        else if (direction === IGroupedRowSortDirection.DESC) {
            rows.sort((row1, row2) => row2.status.localeCompare(row1.status));
        }
    }

    function sortByType(rows: IGroupedRowItem[], direction: IGroupedRowSortDirection): void {
        if (direction === IGroupedRowSortDirection.ASC) {
            rows.sort((row1, row2) => row1.type.localeCompare(row2.type));
        }
        else if (direction === IGroupedRowSortDirection.DESC) {
            rows.sort((row1, row2) => row2.type.localeCompare(row1.type));
        }
    }

    function viewAppointmentInScheduler(row?: IGroupedRowItem) {
        MessageLib.MainWindow.openSchedule();
        // set row as pending to view, then view it when
        // schedulerReady message is received from MessageLib.
        // schedulerReadySink() below will display the appointment
        pageData.pendingRowView = row;
    }

    function schedulerReadySink(): void {
        if (pageData.pendingRowView) {
            MessageLib.Scheduler.loadPatientResourceDate(
                {
                    patientEid: props.patientEid,
                    resourceId: pageData.appointmentResources.find(resource => resource.resourceName == pageData.pendingRowView?.scheduled)?.resourceId,
                    scheduleDate: pageData.pendingRowView?.appointmentDate,
                    appointmentId: pageData.pendingRowView?.appointmentId
                }
            );
            pageData.pendingRowView = undefined;
        }
    }

    function cancelCommentSink(payload: any): void {
        if (pageData.selectedAppointmentId == payload.appointmentId) {
            if (payload.completed) {
                cancelAppointment();
            }
            pageData.showCommentCancel = false;
        }
    }

    async function cancelAppointment(): Promise<void> {
        let cancelStatus = pageData.appointmentStatuses.find(status => status.appointment_status_description === 'Cancelled');
        const payload = {
            patientId: pageData.patientInfo?.patientId ?? 0,
            appointmentId: pageData.selectedAppointmentId ?? 0,
            appointmentStatusId: cancelStatus?.appointment_status_id ?? 0
        }
        const result = await choiceApi.schedule.DashboardUpdateStatus(payload);
        if (!result.passed) {
            popAlert(result.errorMessage ?? "Failed to cancel appointment", "Update Status", ModalStyles.warning);
            return;
        }
        let cancelledAppointment = pageData.appointments?.find(appointment => appointment.appointmentId == pageData.selectedAppointmentId);
        if (cancelledAppointment) {
            cancelledAppointment.statusId = cancelStatus?.appointment_status_id;
        }
    }

    function openCancelComment(row?: IGroupedRowItem): void {
        pageData.selectedAppointmentId = Number(row?.appointmentId);
        if (pageData.selectedAppointmentId) {
            pageData.showCommentCancel = true;
        }
    }

</script>

<style scoped>
    #patientAppointments {
        width: 98vw;
        height: 98vh;
        overflow-x: hidden;
    }
    .custom * {
        font-size: small;
    }
    .footer {
		background-color: var(--bs-border-color);
	}
</style>

<template>
    <div id="patientAppointments" class="bg-white">
        <div class="d-flex w-100 justify-content-end">
            <PatientHeader
                :displayContext="PortalType.Emr"
                :legacyPage="false"
                :patientEntityId="props.patientEid"
                :cacheHeader="false"
                fontSize="smaller"
            />
        </div>
        <div class="fw-bold" style="font-size: small;">{{'Available Filters'}}</div>
        <div class="d-flex w-100 mt-2" style="gap: 2%">
            <div class="d-flex" style="gap: 10px; width: 35%">
                <div>
                    <div class="fw-bold" style="font-size: x-small;">{{'Start Date'}}</div>
                    <DatePickerNoTZ
                        v-model:value="filters.startDate"
                        :clearable="true"
                        :display-format="DateDisplayFormat.NumericDate"
                    />
                </div>
                <div>
                    <div class="fw-bold" style="font-size: x-small;">{{'End Date'}}</div>
                    <DatePickerNoTZ 
                        v-model:value="filters.endDate"
                        :clearable="true"
                        :display-format="DateDisplayFormat.NumericDate"
                    />
                </div>
            </div>
            <div class="d-inline custom" style="margin-top: 1.5%">
                <ChoiceButtonGroup 
                    v-model:selectedValue="filters.quickFilter"
                    :items=quickFilterOptions
                    :button-size="ButtonDisplaySize.XS"
                    :counts="[
                        undefined,
                        getAppointmentCountByStatus('No-Show'),
                        getAppointmentCountByStatus('Cancelled'),
                        getAppointmentCountByStatus('Checked-Out')
                    ]"
                />
            </div>
        </div>
        <div class="d-flex mt-2 pb-3 border-bottom">
            <div class="d-flex" style="gap: 10px; width: 80%">
                <div class="w-25">
                    <div class="fw-bold" style="font-size: x-small;">{{'Status'}}</div>
                    <div class="custom">
                        <v-select
                            v-model="filters.statuses"
                            :options="pageData.appointmentStatuses"
                            :reduce="(item: IAppointmentStatus) => item.appointment_status_id"
                            :multiple="true"
                            :closeOnSelect="false"
                            label="appointment_status_description"
                        />
                    </div>
                </div>
                <div class="w-25">
                    <div class="fw-bold" style="font-size: x-small;">{{'Type'}}</div>
                    <div class="custom">
                        <v-select
                            v-model="filters.types"
                            :options="pageData.appointmentTypes"
                            :reduce="(item: IAppointmentType) => item.appointmentTypeId"
                            :multiple="true"
                            :closeOnSelect="false"
                            label="appointmentName"
                        />
                    </div>
                </div>
                <div class="w-25">
                    <div class="fw-bold" style="font-size: x-small;">{{'Resource'}}</div>
                    <div class="custom">
                        <v-select
                            v-model="filters.resources"
                            :options="pageData.appointmentResources"
                            :reduce="(item: IAppointmentResource) => item.entityId"
                            :multiple="true"
                            :closeOnSelect="false"
                            label="resourceName"
                        />
                    </div>
                </div>
                <div class="w-25">
                    <div class="fw-bold" style="font-size: x-small;">{{'Location'}}</div>
                    <div class="custom">
                        <v-select
                            v-model="filters.locations"
                            :options="pageData.appointmentLocations"
                            :reduce="(item: IAppointmentLocation) => item.locationId"
                            :multiple="true"
                            :closeOnSelect="false"
                            label="locationName"
                        />
                    </div>
                </div>
            </div>
            <div>
                <div class="d-flex ms-3" style="margin-top: 20%">
                    <ChoiceButton
                        :display-style="ButtonDisplayStyle.TextPrimary"
                        @click="resetFilters"
                        :size="ButtonDisplaySize.XS"
                        text="Reset Filters"
                    />
                </div>
            </div>
        </div>
        <div v-if="pageData.appointments" class="mt-3" style="height: 63.5%;">
            <GroupedRowsTable
                :title="'Appointments'"
                :columns="[
                    { name: 'Start', key: 'startTime', widthPercent: 12.5, sortConfig: { sort: sortByStartTime } },
                    { name: 'End', key: 'endTime', widthPercent: 12.5 },
                    { name: 'Scheduled', key: 'scheduled', widthPercent: 12.5, sortConfig: { sort: sortByScheduled } },
                    { name: 'Location', key: 'location', widthPercent: 12.5, sortConfig: { sort: sortByLocation } },
                    { name: 'Status', key: 'status', widthPercent: 12.5, sortConfig: { sort: sortByStatus } },
                    { name: 'Type', key: 'type', widthPercent: 12.5, sortConfig: { sort: sortByType } },
                    { name: 'Chief / Comment', key: 'comment', widthPercent: 12.5 },
                ]"
                :groupedRows="[
                    { groupLabel: 'Future', rows: getFutureAppointments() },
                    { groupLabel: 'Today', rows: getAppointments(DayLookback.Today, DayLookback.Yesterday) },
                    { groupLabel: 'Yesterday', rows: getAppointments(DayLookback.Yesterday, DayLookback.TwoDays) },
                    { groupLabel: 'Last two weeks', rows: getAppointments(DayLookback.TwoDays, DayLookback.TwoWeeks) },
                    { groupLabel: 'Last three weeks', rows: getAppointments(DayLookback.TwoWeeks, DayLookback.ThreeWeeks) },
                    { groupLabel: 'Last 30 days', rows: getAppointments(DayLookback.ThreeWeeks, DayLookback.Thirty) },
                    { groupLabel: 'Older', rows: getAppointments(DayLookback.Thirty) }
                ]"
                :actionConfig="{
                    label: 'Action',
                    columnWidthPercent: 12.5,
                    actions: [
                        { label: 'View', style: ButtonDisplayStyle.TextPrimary, action: viewAppointmentInScheduler },
                        { label: 'Cancel', style: ButtonDisplayStyle.TextDanger, action: openCancelComment }
                    ]
                }"
                :display-footer="true"
            >
                <template #footer>
                    <div v-if="filterIsSet()">{{ `Showing ${filteredAppointments?.length} of ${pageData.appointments.length} records` }}</div>
                    <div v-else>{{ `${pageData.appointments.length} records` }}</div>
                </template>
            </GroupedRowsTable>
        </div>
        <ModalDialog :show="pageData.showCommentCancel" :size="ModalSize.FullScreen" :closeOnClickOutside="false" @hide="() => { pageData.showCommentCancel = false}">
            <template #title>Cancel Comments - {{ pageData.patientInfo?.patientFullName }}</template>
            <CancelCommentLegacy v-if="pageData.selectedAppointmentId" :appointmentId="pageData.selectedAppointmentId" />
        </ModalDialog>
    </div>
</template>

